<script setup lang="ts">
import { type Meeting, EventLocation } from '@/stores/appStore'
import { computed, type PropType } from 'vue'
import googleMeetIcon from '@/assets/icons/google-meet.svg'
import zoomIcon from '@/assets/icons/zoom.svg'
import teamsIcon from '@/assets/icons/teams.svg'
const meetings = defineModel('meetings', { required: true, type: Array as PropType<Meeting[]> })
const { maxView, showRemoved, showPast } = defineProps<{
  maxView: null | number
  showRemoved: boolean
  showPast: boolean
}>()
const displayMeetings = computed(() => {
  const filtered = meetings.value.filter((meeting) => {
    if (!showRemoved && meeting.status === 'cancelled') {
      return false
    }
    if (!showPast && new Date(meeting.start) < new Date()) {
      return false
    }
    return true
  })
  return maxView ? filtered.slice(0, maxView) : filtered
})

const hoursMinutesFormatter = new Intl.DateTimeFormat('en-US', {
  hour: 'numeric',
  minute: 'numeric',
  hour12: true
})

const dateTimeFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
  hour12: true
})

const formatMeetingTime = (meetingDate: Date) => {
  const today = new Date()
  const isToday = meetingDate.toDateString() === today.toDateString()
  return isToday ? hoursMinutesFormatter.format(meetingDate) : dateTimeFormatter.format(meetingDate)
}

const calcDuration = (meeting: Meeting) => {
  const start = new Date(meeting.start)
  const end = new Date(meeting.end)
  return `${Math.round((end.getTime() - start.getTime()) / (1000 * 60))}min`
}

const getStatusColor = (meeting: Meeting) => {
  if (meeting.status === 'cancelled') {
    return 'text-secondary text-strike'
  }
  if (new Date(meeting.start) < new Date()) {
    return 'text-accent'
  }
  return 'text-primary'
}
</script>

<template>
  <div class="row q-col-gutter-md full-width">
    <div
      class="col-12 col-sm-6 col-md paddingFixer3rd"
      v-for="meeting in displayMeetings"
      :key="meeting.eventID"
      style="min-width: 30%"
    >
      <a :href="`/events/${meeting.eventID}`">
        <div class="myCard q-pa-md">
          <div class="row justify-between q-mb-md">
            <div class="flex items-center">
              <span :class="getStatusColor(meeting)">{{ formatMeetingTime(new Date(meeting.start)) }}</span>
              <q-badge v-if="meeting.status === 'cancelled'" class="q-ml-sm" color="secondary" label="Cancelled" />
              <q-badge v-if="new Date(meeting.start) < new Date()" class="q-ml-sm" color="accent" label="Past" />
            </div>
            <q-badge :label="calcDuration(meeting)" color="black" text-color="text-grey-6" align="middle" />
          </div>
          <p class="text-bold text-subtitle1 text-white text-truncate">{{ meeting.title }}</p>
          <div v-if="meeting.location" class="text-grey-6">
            <q-icon tag="img" :src="zoomIcon" v-if="meeting.location === EventLocation.ZOOM" />
            <q-icon tag="img" :src="teamsIcon" v-else-if="meeting.location === EventLocation.TEAMS" />
            <q-icon tag="img" :src="googleMeetIcon" v-else-if="meeting.location === EventLocation.GOOGLE_MEET" />
            <q-icon name="mdi-skype-business" v-else-if="meeting.location === EventLocation.SKYPE" />
            <q-icon
              name="mdi-office-building-marker-outline"
              v-else-if="meeting.location === EventLocation.IN_PERSON"
            />
            <q-icon name="mdi-video-account" v-else />
            <!-- online -->
            <span class="q-ml-sm text-capitalize">{{
              meeting.location.replace('_', ' ').replace('online', 'Video Call')
            }}</span>
          </div>
          <div class="row q-mt-sm justify-between">
            <div style="height: 28px">
              <q-avatar
                v-for="(attendee, idx) in meeting.attendees
                  .filter((attendee) => attendee.initial !== '' || attendee.picture)
                  .slice(0, 6)"
                :key="idx"
                size="28px"
                class="q-mr-xs overlapping"
                :style="`left: -${idx * 15}px`"
                :color="idx % 2 === 0 ? 'accent' : 'secondary'"
              >
                <img v-if="attendee.picture" :src="attendee.picture" />
                <span v-else class="text-white">{{ attendee.initial.toUpperCase() }}</span>
              </q-avatar>
            </div>
            <span class="text-grey-6 self-center">{{ meeting.attendees.length }} Attendees</span>
          </div>
        </div>
      </a>
    </div>
  </div>
</template>

<style scoped>
a {
  display: block;
  height: fit-content;
  padding: 0;
}
</style>
